// Packages
import feather from "feather-icons"
import { motion } from "framer-motion"
import * as React from "react"
import { twMerge } from "tw-merge"

// Utils
import { useWindowWidth } from "@react-hook/window-size"

// Props
const defaultProps = {
  showControlButtons: true,
  showIndicatorButtons: true,
}
type ElementClassesType = {
  controlButton?: string
  controlButtonsContainer?: string
  controlIcon?: string
  indicatorButton?: string
  indicatorButtonsContainer?: string
  slideItem?: string
  titleContainer?: string
  titleFullWidthContainer?: string
  track?: string
  trackContainer?: string
}
type Props = {
  classes?: {
    active?: ElementClassesType
    default?: ElementClassesType
  }
  indicatorButton?: React.ReactElement
  useFixedTrackHeight?: boolean
  nextControlButton?: React.ReactElement
  nextControlIcon?: React.ReactNode
  previousControlButton?: React.ReactElement
  previousControlIcon?: React.ReactElement
  showControlButtons?: boolean
  showIndicatorButtons?: boolean
  slides: Array<React.ReactNode>
  title?: React.ReactNode
}

// Default Component
export default function Slider(propsPassed: Props) {
  const props = {
    ...defaultProps,
    ...propsPassed,
  }
  const [activeSlide, setActiveSlide] = React.useState(0)
  const [isDragging, setIsDragging] = React.useState(false)
  const [slideItemWidth, setSlideItemWidth] = React.useState(null)
  const [trackColumnGap, setTrackColumnGap] = React.useState(null)
  const [trackHeight, setTrackHeight] = React.useState(0)
  const trackContainerRef = React.useRef(null)
  const trackRef = React.useRef(null)
  const activeSlideItemRef = React.useRef(null)
  const [breakpoints, setBreakpoints] = React.useState([])
  const windowWidth = useWindowWidth()

  React.useEffect(() => {
    const updateSliderValues = () => {
      const slideItemWidthNum = parseInt(
        window
          .getComputedStyle(activeSlideItemRef.current)
          .getPropertyValue("width"),
      )
      const trackWidthNum = parseInt(
        window.getComputedStyle(trackRef.current).getPropertyValue("width"),
      )
      const trackWidthMinusSlideItems =
        trackWidthNum - slideItemWidthNum * props.slides.length
      const trackColumnGapNum =
        trackWidthMinusSlideItems / (props.slides.length - 1)

      setSlideItemWidth(Math.round(slideItemWidthNum))
      setTrackColumnGap(Math.round(trackColumnGapNum))
    }
    updateSliderValues()
  }, [windowWidth])

  React.useEffect(() => {
    let maxHeight = 0
    let slideListItems = trackRef.current.querySelectorAll("li")
    for (let i = 0; i < slideListItems.length; i++) {
      const currentSlideListItemHeight = slideListItems[i].clientHeight
      if (currentSlideListItemHeight > maxHeight)
        maxHeight = currentSlideListItemHeight
    }
    if (maxHeight) setTrackHeight(maxHeight)
  }, [trackContainerRef?.current, props.slides])

  React.useEffect(() => {
    const getBreakpoints = () => {
      const allBreakpoints = [0]
      let currentBreakpoint = 0
      for (let i = 0; i < props.slides.length; i++) {
        currentBreakpoint = currentBreakpoint - slideItemWidth - trackColumnGap
        allBreakpoints.push(currentBreakpoint)
      }
      setBreakpoints(allBreakpoints)
      setActiveSlide(0)
    }
    getBreakpoints()
  }, [slideItemWidth])

  const previousControlButtonAttributes = {
    disabled: activeSlide === 0 || isDragging,
    onClick: () => setActiveSlide(prevActiveSlide => prevActiveSlide - 1),
  }
  const nextControlButtonAttributes = {
    disabled: activeSlide === props.slides.length - 1 || isDragging,
    onClick: () => setActiveSlide(prevActiveSlide => prevActiveSlide + 1),
  }

  const completeControlButtonDefaultClass = twMerge(
    `disabled:bg-white disabled:border-grey-4 disabled:cursor-not-allowed group/control-button flex h-[32px]  w-[32px] items-center justify-center rounded-full border border-brand bg-brand transition-all  ${
      props.classes?.default?.controlButton || ""
    }`,
  )
  const completeControlIconDefaultClass = twMerge(
    `stroke-white group-disabled/control-button:stroke-grey-4 h-[20px] w-[20px] ${
      props.classes?.default?.controlIcon || ""
    }`,
  )
  return (
    <>
      {(props.title || props.showControlButtons) && (
        <div
          className={twMerge(
            `w-full ${props.classes?.default.titleFullWidthContainer || ""}`,
          )}
        >
          <div
            className={twMerge(
              `mb-[16px] flex max-w-[1152px] items-center justify-between ${
                props.classes?.default.titleContainer || ""
              }`,
            )}
          >
            {props.title && props.title}
            {/* Control Buttons Container */}
            {props.showControlButtons && (
              <div
                className={twMerge(
                  `mx-0 flex items-center justify-start gap-[16px] p-[8px] ${
                    props.classes?.default.controlButtonsContainer || ""
                  }`,
                )}
              >
                {props.previousControlButton ? (
                  React.cloneElement(props.previousControlButton, {
                    ...previousControlButtonAttributes,
                  })
                ) : (
                  <button
                    className={completeControlButtonDefaultClass}
                    {...previousControlButtonAttributes}
                  >
                    {props.previousControlIcon || (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: feather.icons["arrow-left"].toSvg({
                            class: completeControlIconDefaultClass,
                          }),
                        }}
                      />
                    )}
                  </button>
                )}
                {props.nextControlButton ? (
                  React.cloneElement(props.nextControlButton, {
                    ...nextControlButtonAttributes,
                  })
                ) : (
                  <button
                    className={completeControlButtonDefaultClass}
                    {...nextControlButtonAttributes}
                  >
                    {props.nextControlIcon || (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: feather.icons["arrow-right"].toSvg({
                            class: completeControlIconDefaultClass,
                          }),
                        }}
                      />
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Slide Track Container */}
      <div
        className={twMerge(`${props.classes?.default?.trackContainer || ""}`)}
        ref={trackContainerRef}
        style={{
          height:
            props.useFixedTrackHeight && trackHeight > 0 && `${trackHeight}px`,
        }}
      >
        {/* Slide Track */}
        <motion.ul
          animate={{
            x: breakpoints[activeSlide],
          }}
          className={twMerge(
            `${
              isDragging ? "cursor-grabbing" : "cursor-grab"
            } max-w-max relative w-max flex touch-none list-none items-center overflow-hidden ${
              props.classes?.default?.track || ""
            }`,
          )}
          drag="x"
          dragConstraints={{
            left: -trackContainerRef.current?.clientWidth + slideItemWidth,
            right: 0,
          }}
          dragDirectionLock
          dragElastic={0}
          dragTransition={{
            modifyTarget: target =>
              Math.round(target / (slideItemWidth + trackColumnGap)) *
              (slideItemWidth + trackColumnGap),
            power: 0,
            timeConstant: 10,
          }}
          onDragStart={() => setIsDragging(true)}
          onDragTransitionEnd={() => {
            const trackStyle = window.getComputedStyle(trackRef?.current)
            const matrix = new DOMMatrixReadOnly(trackStyle.transform)
            const trackTranslateXPosition = Math.abs(matrix.m41)
            breakpoints.forEach((breakpoint, i) => {
              if (Math.abs(breakpoint) === trackTranslateXPosition) {
                setActiveSlide(i)
                setIsDragging(false)
              }
            })
          }}
          ref={trackRef}
          // style={{
          //   columnGap: `${trackColumnGap}px`,
          // }}
          style={{
            height:
              props.useFixedTrackHeight &&
              trackHeight > 0 &&
              `${trackHeight}px`,
          }}
          transition={{ duration: 0.5, ease: [0.04, 0.62, 0.23, 0.98] }}
        >
          {/* Slides */}
          {props.slides.map((slide: React.ReactElement, i) => {
            return (
              <li
                key={`slide-${i}`}
                className={twMerge(
                  `${props.classes?.default?.slideItem || ""} ${
                    i === activeSlide ? props.classes?.active?.slideItem : ""
                  }`,
                )}
                ref={i === activeSlide ? activeSlideItemRef : undefined}
                // style={{
                //   width: `${slideItemWidth}px`,
                // }}
              >
                {React.cloneElement(slide, { activeSlideIndex: activeSlide })}
              </li>
            )
          })}
        </motion.ul>
      </div>
      {/* Indicator Controls Container */}
      {props.showIndicatorButtons && (
        <div
          className={twMerge(
            `ms-[84px] mt-[24px] flex w-fit gap-[8px] px-[8px] py-[22px] ${
              props.classes?.default?.indicatorButtonsContainer || ""
            }`,
          )}
        >
          {props.slides.map((_, index) => {
            const key = `indicator-button-${index}`
            const targetSlide = () => {
              setActiveSlide(index)
            }
            return props.indicatorButton ? (
              React.cloneElement(props.indicatorButton, {
                className: `${
                  index === activeSlide
                    ? props.classes?.active?.indicatorButton || ""
                    : ""
                } ${props.classes?.default?.indicatorButton || ""}`,
                key,
                onClick: targetSlide,
              })
            ) : (
              <button
                key={key}
                onClick={targetSlide}
                className={`h-1 rounded-[1px] ${
                  index === activeSlide
                    ? props.classes?.active?.indicatorButton || "w-12 bg-brand"
                    : "w-4 bg-quarternary"
                }  ${props.classes?.default?.indicatorButton || ""}`}
              />
            )
          })}
        </div>
      )}
    </>
  )
}
